import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { AppContext } from '../../context';
import { RecPageProvider, useRecPage } from './context';
import SelectionPanel from './SelectionPanel';
import TabBar from './TabBar';
import FiltersRail from './FiltersRail';
import FreshnessPill from './FreshnessPill';
import StaleBanner from './StaleBanner';
import MaintenanceBanner from './MaintenanceBanner';
import BatchControlCard from './BatchControlCard';
import SearchModeControl from './SearchModeControl';
import ResultsPanel from './ResultsPanel';
import CandidateWorkspacePanel from '../../components/CandidateWorkspacePanel';
import { SET_STATUS_OPTIONS } from '../../utils/constant';
import { recsysClient } from './recsysClient';
import { CANDIDATES_BY_IDS, INTERVIEWED_CANDIDATE_IDS_FOR_JOB, JOB_APPLICANTS_FOR_CANDIDATES } from './gql';
import { filterHiddenRows as filterHiddenRowsWith } from './hiddenRows';
import {
  applicantPanelProps,
  buildRecsysFilters,
  classifySearchError,
  matchBand,
  poolInfoFromResponse,
  scoreBand,
  SearchBackendError,
} from './utils';
import {
  AtsCandidate,
  HydratedRow,
  NormalizedRow,
  RecsysApplicantPool,
  RecsysHttpError,
  RecsysRowApplicant,
  ApplicantScoreUpdate,
} from './types';

// Pagination + progressive-hydration pacing (README §Interactions & Behavior).
const PAGE_SIZE = 8;
const HYDRATE_STAGGER_MS = 85;

// Collaborated Search (kan-82): most-recent interviewed candidates used as
// similarity seeds. recsys rejects more than 16 seeds; the page sends at most
// this many.
const MAX_COLLAB_SEEDS = 15;

// Collaborated Search run card — same visual tone as BatchControlCard (white,
// 1px #E2E5EA, radius 8, subtle shadow).
const COLLAB_CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E5EA',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
};

// README §Global layout / app shell "Header": 56px, white, 1px bottom
// border, padding 0 24px, title + subtitle on the left and a right-aligned
// freshness pill.
const HEADER_STYLE: React.CSSProperties = {
  height: 56,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid #E2E5EA',
  background: '#fff',
  padding: '0 24px',
};

const emptyHydratedRow = (row: NormalizedRow): HydratedRow => ({
  row,
  atsLoaded: false,
  profileStatus: 'idle',
});

/**
 * Page content — the sole consumer of useRecPage(). Owns the batch/search
 * fetch + two-source progressive hydration (STORY-2A-SPEC.md rules 2, 3, 6)
 * and maps context state onto the exact prop contracts declared in ./types.ts
 * for every Stage-1 component, so those stay plain and prop-driven.
 */
const CandidateRecommendationsContent: FC = () => {
  const ctx = useRecPage();
  // Pulled off the context so the run callbacks below depend on this one
  // function rather than the whole context object (kan-80). setPoolInfo is a
  // state setter (stable) — same reasoning (kan-119).
  const { persistSearchSettings, setPoolInfo } = ctx;
  // kan-119 "Search in: New applicants". Only meaningful with a job selected
  // (the toggle only renders then, and the context resets it on job change).
  // In this mode every pool tab sends the applicantPool descriptor — the ATS
  // proxy resolves it to the job's Applied applicants server-side — and the
  // rows act on the JobApplicant (status / decline), not on dispositions.
  const applicantMode = ctx.poolScope === 'applicants' && !!ctx.jobId;
  // Memoized so the run callbacks below only re-create when the pool itself
  // changes. statuses omitted = the proxy's default, ['Applied'].
  const applicantPool: RecsysApplicantPool | undefined = useMemo(
    () => (applicantMode && ctx.jobId ? { jobOrderId: ctx.jobId } : undefined),
    [applicantMode, ctx.jobId]
  );
  const apolloClient = useApolloClient();
  // Index freshness (pill) + the stale-index banner / "Refresh now" affordance
  // are ops-level and gated to Executives only. The context userRoles are
  // UPPERCASE (e.g. 'EXECUTIVE' — see helper.ts / CustomiseDashboard usage), but
  // some call sites use title-case, so match case-insensitively to be safe.
  const { userRoles } = useContext(AppContext);
  const isExecutive = !!userRoles?.some((r) => typeof r === 'string' && r.toUpperCase() === 'EXECUTIVE');

  const [rows, setRows] = useState<NormalizedRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [tooBroad, setTooBroad] = useState(false);
  // Last run failed at the backend (recsys/proxy 5xx, network) — shown as an
  // explicit error card instead of "No matches found" (kan-119 follow-up:
  // the nightly maintenance outage was indistinguishable from an empty search).
  const [backendError, setBackendError] = useState<SearchBackendError | null>(null);
  // A stale error must not outlive the context it happened in: switching tab,
  // job order or search scope starts fresh (each run also clears it on start).
  useEffect(() => {
    setBackendError(null);
  }, [ctx.view, ctx.jobId, ctx.poolScope]);
  const [briefUsed, setBriefUsed] = useState(false);
  const [computedAt, setComputedAt] = useState<string | null>(null);
  // Batch Matches LLM-score partition (client reorders by llmScore, server
  // vector order preserved on the wire): matchCount is how many leading rows
  // in `rows` are matches (llmScore >= 50), the rest are non-matches hidden
  // behind the "Show N non-matches" expander. Search/Shortlist/Declined don't
  // use this — see effectiveTotal below.
  const [matchCount, setMatchCount] = useState(0);
  const [showNonMatches, setShowNonMatches] = useState(false);
  const [hydratedMap, setHydratedMap] = useState<Record<string, HydratedRow>>({});
  // Shortlist-tab bulk-apply multi-select (Story 2b). Cleared on view change
  // and after every bulk apply.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  // Batch Matches is explicit-run (like Search Pool): after the recruiter picks
  // a company + job we wait for an explicit Refresh before running the match, so
  // selecting a job never auto-fires a run. True = job selected but not yet run.
  const [batchAwaitingRun, setBatchAwaitingRun] = useState(true);
  // Collaborated Search (kan-82) is explicit-run too. collabNoInterviews marks
  // the gated case (job order has zero interviews — recsys is never called);
  // collabSeeds carries the "Based on N of M interviewed candidates." counts.
  const [collabAwaitingRun, setCollabAwaitingRun] = useState(true);
  const [collabNoInterviews, setCollabNoInterviews] = useState(false);
  const [collabSeeds, setCollabSeeds] = useState<{ used: number; total: number } | null>(null);

  const fetchTokenRef = useRef(0);
  const hydrateTokenRef = useRef(0);
  // topN is read through a ref so changing the "Results (Top X)" select does
  // NOT re-create runBatch/runSearch — which would otherwise re-trigger the
  // auto-run effect and reset the result list (clearing it on the Search tab).
  // The new count applies on the next explicit Refresh / Search instead.
  const topNRef = useRef(ctx.topN);
  topNRef.current = ctx.topN;

  /**
   * Drops rows the recruiter must not see before they render — the applied/
   * dispositioned exclusion and the private-candidate gate. See hiddenRows.ts
   * for the rules (and why it fails open). In applicant mode (kan-119) the
   * applied exclusion is skipped: the pool IS the job's applicants, so it
   * would hide every row. The private-candidate gate always runs.
   */
  const filterHiddenRows = useCallback(
    (jobId: string | null, candidateRows: NormalizedRow[], skipApplied = false): Promise<NormalizedRow[]> =>
      filterHiddenRowsWith(apolloClient, jobId, candidateRows, { skipApplied }),
    [apolloClient]
  );

  // Fetch index freshness once on mount — feeds the header's FreshnessPill and
  // the StaleBanner beneath it.
  useEffect(() => {
    let cancelled = false;
    recsysClient
      .ingestStatus()
      .then((status) => {
        if (!cancelled) ctx.setIngest(status);
      })
      .catch(() => {
        if (!cancelled) ctx.setIngest(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Batch tab: always a live `/match` call with `explain: true` — the
   * LLM-score-driven matching change retired the nightly-cache-first
   * `/jobs/:id/matches` shortcut here, since that endpoint returns candidates
   * with no `explain`, which would render every row unscored/hidden. briefUsed
   * is therefore always false and computedAt always null for Batch now.
   * Candidates come back in similarity-descending order (server rerank stays
   * OFF); this function partitions + reorders them by LLM score client-side.
   */
  const runBatch = useCallback(async () => {
    if (!ctx.jobId) {
      setRows([]);
      setMatchCount(0);
      return;
    }
    // Executed search: save this configuration against the job order (kan-80).
    // Fire-and-forget inside the context — never blocks or fails the run.
    persistSearchSettings('batch');
    // An explicit run has started (Refresh button / job-order change) — drop the
    // "hit Refresh" prompt so results (or a real empty state) render.
    setBatchAwaitingRun(false);
    setShowNonMatches(false);
    const token = ++fetchTokenRef.current;
    setRowsLoading(true);
    setTooBroad(false);
    setBackendError(null);
    try {
      // Over-fetch 2x the display count (recsys clamps at 100) so exclusion
      // filtering still leaves headroom; ctx.topN itself stays the retrieval
      // hint (Batch no longer slices display to it post-partition — see the
      // partition below).
      const matchArgs = {
        jobDescription: ctx.jobDescription,
        topN: Math.min(topNRef.current * 2, 100),
        filters: buildRecsysFilters(ctx.filters),
        // OMIT when empty — recsys rejects `mustHaveTerms: []` with a 400
        // ("must be a non-empty array"), which the outer catch would turn into
        // a false "no matches". Only send the field when there are terms.
        mustHaveTerms: ctx.mustHave.length ? ctx.mustHave : undefined,
        // kan-119 applicant mode: restrict the match to the job's new applicants.
        applicantPool,
      };
      let live;
      try {
        live = await recsysClient.match({ ...matchArgs, explain: true });
      } catch (matchErr) {
        // AI-explain 400s when RECSYS_GENERATIVE_ENDPOINT is unset on the
        // deployment (STORY-2A-SPEC rule 5). Degrade gracefully: retry once
        // WITHOUT explain so the recruiter still sees the ranked candidates,
        // just all unscored (no LLM band) — never a blank result.
        if (matchErr instanceof RecsysHttpError && matchErr.status === 400) {
          live = await recsysClient.match({ ...matchArgs, explain: false });
        } else {
          throw matchErr;
        }
      }
      if (fetchTokenRef.current !== token) return;
      setPoolInfo(poolInfoFromResponse(live));
      setBriefUsed(false);
      setComputedAt(null);
      const liveRows: NormalizedRow[] = live.candidates.map((c, idx) => {
        // JUDGED + scored only when explain is present AND status==='ok' AND
        // llmScore is a number (shared contract). Judged-but-failed
        // (status!=='ok') and unjudged (explain absent) both land in the
        // non-match bucket below.
        const llmScore = c.explain?.status === 'ok' ? c.explain.llmScore : null;
        return {
          candidateId: c.candidateId,
          rank: idx + 1,
          score: c.score,
          band: scoreBand(c.score),
          maxSim: c.maxSim,
          meanTop3: c.meanTop3,
          matchedChunkCount: c.matchedChunks?.length ?? 0,
          isBooleanMatch: false,
          updatedAt: null,
          explain: c.explain,
          llmScore,
          judged: c.explain !== undefined,
          matchBand: matchBand(llmScore),
        };
      });
      const filteredLiveRows = await filterHiddenRows(ctx.jobId, liveRows, !!applicantPool);
      if (fetchTokenRef.current !== token) return;

      // Partition + reorder by LLM score (client-side; the wire order stays
      // similarity-descending). Matches (llmScore >= 50) sort first by
      // llmScore desc, then maxSim desc, then candidateId asc (stable
      // tie-break). Non-matches follow: scored-but-below-50 first (same
      // sort), then unscored/judged-but-failed by maxSim desc, candidateId asc.
      const byScoreThenSim = (a: NormalizedRow, b: NormalizedRow): number =>
        (b.llmScore as number) - (a.llmScore as number) ||
        (b.maxSim ?? -Infinity) - (a.maxSim ?? -Infinity) ||
        a.candidateId.localeCompare(b.candidateId);
      const bySimThenId = (a: NormalizedRow, b: NormalizedRow): number =>
        (b.maxSim ?? -Infinity) - (a.maxSim ?? -Infinity) || a.candidateId.localeCompare(b.candidateId);

      const matches = filteredLiveRows
        .filter((r) => r.llmScore != null && r.llmScore >= 50)
        .sort(byScoreThenSim);
      const nonMatchesScored = filteredLiveRows
        .filter((r) => r.llmScore != null && r.llmScore < 50)
        .sort(byScoreThenSim);
      const nonMatchesUnscored = filteredLiveRows.filter((r) => r.llmScore == null).sort(bySimThenId);
      const nonMatches = [...nonMatchesScored, ...nonMatchesUnscored];

      setRows([...matches, ...nonMatches].map((r, i) => ({ ...r, rank: i + 1 })));
      setMatchCount(matches.length);
    } catch (err) {
      // AI-explain / useBrief can 400 when RECSYS_GENERATIVE_ENDPOINT is unset
      // (handled above by the explain:false retry); anything else — recsys
      // 5xx, the proxy's 502 pool guard, a network failure — is a BACKEND
      // error and renders as one, never as "no matches".
      if (fetchTokenRef.current === token) {
        setRows([]);
        setMatchCount(0);
        setBackendError(classifySearchError(err));
      }
    } finally {
      if (fetchTokenRef.current === token) setRowsLoading(false);
    }
    // ctx.topN intentionally excluded — read via topNRef so a page-size change
    // doesn't re-run the match / reset the list (applies on next Refresh).
  }, [
    ctx.jobId,
    ctx.jobDescription,
    ctx.filters,
    ctx.mustHave,
    applicantPool,
    persistSearchSettings,
    setPoolInfo,
    filterHiddenRows,
  ]);

  /** Search tab: explicit trigger only (Search button), never live-as-you-type. */
  const runSearch = useCallback(async () => {
    if (!ctx.search.query.trim()) {
      setRows([]);
      return;
    }
    // Executed search: save this configuration against the job order (kan-80).
    // No-ops when no job order is selected (search runs without one).
    persistSearchSettings('search');
    const token = ++fetchTokenRef.current;
    setRowsLoading(true);
    setTooBroad(false);
    setBackendError(null);
    try {
      const result = await recsysClient.search({
        query: ctx.search.query,
        mode: ctx.search.mode,
        filters: buildRecsysFilters(ctx.filters),
        // Over-fetch 2x the display count (recsys clamps at 100) so exclusion
        // filtering still leaves topN rows to show; the display count stays the
        // selected Top-N (read via topNRef so changing it doesn't reset the list).
        topN: Math.min(topNRef.current * 2, 100),
        applicantPool,
      });
      if (fetchTokenRef.current !== token) return;
      setPoolInfo(poolInfoFromResponse(result));
      const searchRows: NormalizedRow[] = result.candidates.map((c, idx) => ({
        candidateId: c.candidateId,
        rank: idx + 1,
        score: c.score,
        band: scoreBand(c.score),
        maxSim: c.maxSim,
        meanTop3: c.meanTop3,
        matchedChunkCount: 0,
        isBooleanMatch: ctx.search.mode === 'boolean',
        updatedAt: c.updatedAt,
        llmScore: null,
        judged: false,
        matchBand: null,
      }));
      const filteredSearchRows = await filterHiddenRows(ctx.jobId, searchRows, !!applicantPool);
      if (fetchTokenRef.current !== token) return;
      setRows(filteredSearchRows.slice(0, topNRef.current).map((r, i) => ({ ...r, rank: i + 1 })));
    } catch (err) {
      if (fetchTokenRef.current !== token) return;
      // Generic HTTP 400 = over-broad guardrail (no `query_too_broad` code
      // string on the wire — detect by status only, per STORY-2A-SPEC rule 3).
      if (err instanceof RecsysHttpError && err.status === 400) {
        setTooBroad(true);
      } else {
        setBackendError(classifySearchError(err));
      }
      setRows([]);
    } finally {
      if (fetchTokenRef.current === token) setRowsLoading(false);
    }
    // ctx.topN intentionally excluded (read via topNRef) — see runBatch.
  }, [
    ctx.search.query,
    ctx.search.mode,
    ctx.filters,
    ctx.jobId,
    applicantPool,
    persistSearchSettings,
    setPoolInfo,
    filterHiddenRows,
  ]);

  /**
   * Collaborated Search tab (kan-82): candidates whose résumés are similar to
   * the ones already interviewed on this job order. Explicit-run.
   *
   * The interviewed-seed fetch GATES the run — a job order with no interviews
   * shows a dedicated empty state and never reaches recsys. Deliberately does
   * NOT call persistSearchSettings: this tab adds no saved settings (kan-80).
   */
  const runCollab = useCallback(async () => {
    if (!ctx.jobId) {
      setRows([]);
      return;
    }
    setCollabAwaitingRun(false);
    setCollabNoInterviews(false);
    setCollabSeeds(null);
    const token = ++fetchTokenRef.current;
    setRowsLoading(true);
    setTooBroad(false);
    setBackendError(null);
    try {
      // Seeds first — network-only, the interview set moves under us.
      const seedRes = await apolloClient.query<{ interviewedCandidateIdsForJob: string[] }>({
        query: INTERVIEWED_CANDIDATE_IDS_FOR_JOB,
        variables: { jobOrderId: ctx.jobId },
        fetchPolicy: 'network-only',
      });
      if (fetchTokenRef.current !== token) return;
      // Server returns them most-recent-interview-first; take the newest 15.
      const interviewedIds = seedRes.data.interviewedCandidateIdsForJob;
      const seeds = interviewedIds.slice(0, MAX_COLLAB_SEEDS);
      if (!seeds.length) {
        setCollabNoInterviews(true);
        setRows([]);
        return;
      }

      const result = await recsysClient.similar({
        seedCandidateIds: seeds,
        // The filters rail composes unchanged; no must-have terms on this tab.
        filters: buildRecsysFilters(ctx.filters),
        // Over-fetch 2x the display count (recsys clamps at 100) so exclusion
        // filtering still leaves topN rows to show.
        topN: Math.min(topNRef.current * 2, 100),
        applicantPool,
      });
      if (fetchTokenRef.current !== token) return;
      setPoolInfo(poolInfoFromResponse(result));
      // Indicator counts: `used` comes from the response stats; `total` is ALL
      // interviewed candidates (pre-slice), so 40 interviews reads "15 of 40",
      // not "15 of 15". stats.seedsRequested can only count the sliced list we
      // sent, so it cannot supply M; skippedSeedIds is likewise surfaced only
      // through the used/total gap, not listed.
      // The proxy's empty-pool short-circuit (kan-119) answers `stats: {}` —
      // recsys never ran, so no seed was used.
      setCollabSeeds({ used: result.stats?.seedsUsed ?? 0, total: interviewedIds.length });

      const collabRows: NormalizedRow[] = result.candidates.map((c, idx) => ({
        candidateId: c.candidateId,
        rank: idx + 1,
        // The wire `score` is the FUSED RRF value — a rank scale (~1/61 per
        // seed list), meaningless on the 0..1 similarity bar. Display maxSim
        // instead (Search Pool tone); ordering stays the server's fused order,
        // preserved by array position.
        score: c.maxSim,
        band: scoreBand(c.maxSim),
        maxSim: c.maxSim,
        meanTop3: c.meanTop3,
        matchedChunkCount: c.matchedChunks?.length ?? 0,
        isBooleanMatch: false,
        updatedAt: null,
        // No LLM scoring / explanations / badges on this tab.
        llmScore: null,
        judged: false,
        matchBand: null,
      }));
      const filteredCollabRows = await filterHiddenRows(ctx.jobId, collabRows, !!applicantPool);
      if (fetchTokenRef.current !== token) return;
      setRows(filteredCollabRows.slice(0, topNRef.current).map((r, i) => ({ ...r, rank: i + 1 })));
    } catch (err) {
      // Seed-query or recsys failures render as a backend error (the shared
      // error state the earlier note anticipated), not as "No matches found".
      if (fetchTokenRef.current === token) {
        setRows([]);
        setBackendError(classifySearchError(err));
      }
    } finally {
      if (fetchTokenRef.current === token) setRowsLoading(false);
    }
    // ctx.topN intentionally excluded (read via topNRef) — see runBatch.
  }, [ctx.jobId, ctx.filters, applicantPool, apolloClient, setPoolInfo, filterHiddenRows]);

  // Batch Matches is EXPLICIT-run (like Search Pool): entering the tab or
  // changing the selected company/job clears the list and shows a "hit Refresh"
  // prompt — it does NOT auto-run the match. Recruiters pick company + job
  // first, then hit Refresh (or the stale-index banner's Refresh) to run it.
  // Filter/must-have edits also wait for the next Refresh. A "Search in" scope
  // change (kan-119) counts as a job change.
  useEffect(() => {
    if (ctx.view === 'batch') {
      setRows([]);
      setRowsLoading(false);
      setTooBroad(false);
      setBatchAwaitingRun(true);
      setMatchCount(0);
      setShowNonMatches(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.view, ctx.jobId, ctx.poolScope]);

  // Collaborated Search is explicit-run as well: entering the tab or changing
  // the selected job clears the list and the seed accounting, and waits for
  // "Find Similar Candidates". Filter edits also wait for the next run. A
  // scope change (kan-119) counts as a job change here too.
  useEffect(() => {
    if (ctx.view === 'collab') {
      setRows([]);
      setRowsLoading(false);
      setTooBroad(false);
      setCollabAwaitingRun(true);
      setCollabNoInterviews(false);
      setCollabSeeds(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.view, ctx.jobId, ctx.poolScope]);

  // Switching TO the Search tab starts fresh (results wait for an explicit
  // Search). Keyed on view (+ the kan-119 scope: results from the other pool
  // must not linger under the new one), so it does NOT clear on filter edits
  // or on a switch to Shortlist/Declined.
  useEffect(() => {
    if (ctx.view === 'search') {
      setRows([]);
      setTooBroad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.view, ctx.poolScope]);

  // A scope flip (kan-119) also orphans any run still in flight: bump the
  // fetch token so its response — rows AND pool numbers — is dropped instead
  // of landing under the other scope. The clears above already reset the list.
  const poolScopeMountedRef = useRef(false);
  useEffect(() => {
    if (!poolScopeMountedRef.current) {
      poolScopeMountedRef.current = true;
      return;
    }
    fetchTokenRef.current += 1;
    setRowsLoading(false);
  }, [ctx.poolScope]);

  // Shortlist / Declined tabs (Story 2b): not a recsys fetch — rows come
  // straight from the disposition ids in context, in minimal NormalizedRow
  // form. The existing candidatesByIds hydration effect below keys off
  // `visibleRows`, so it hydrates these rows unchanged.
  useEffect(() => {
    if (ctx.view !== 'shortlist' && ctx.view !== 'declined') return;
    const ids = ctx.view === 'shortlist' ? ctx.shortlistIds : ctx.declinedIds;
    setRows(
      ids.map((candidateId, i) => ({
        candidateId,
        rank: i + 1,
        score: null,
        band: null,
        maxSim: null,
        meanTop3: null,
        matchedChunkCount: 0,
        isBooleanMatch: false,
        updatedAt: null,
        llmScore: null,
        judged: false,
        matchBand: null,
      }))
    );
    setRowsLoading(false);
  }, [ctx.view, ctx.shortlistIds, ctx.declinedIds]);

  // Clear the bulk-apply selection whenever the recruiter leaves the
  // Shortlist tab (switches views, or the row set changes underneath it).
  useEffect(() => {
    setSelectedIds(new Set());
  }, [ctx.view]);

  // Search Pool: once a search is active, editing a filter RE-RUNS the search
  // (with the new filter) instead of clearing it. Keyed on the recruiter-edit
  // counter, NOT the filters object: programmatic filter applies — the kan-78
  // job-geo prefill, the kan-80 job-switch reset, and above all a saved
  // settings row loading from another user — must never fire a search on
  // their own. Only the Search button or a real filter edit does. Query and
  // mode changes still require an explicit Search, so this never fires while
  // the recruiter is typing.
  useEffect(() => {
    if (ctx.view === 'search' && ctx.search.query.trim()) {
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.filterEditCount]);

  const handleRefresh = useCallback(() => {
    if (ctx.view === 'batch') {
      runBatch();
    } else if (ctx.view === 'collab') {
      runCollab();
    } else if (ctx.search.query.trim()) {
      runSearch();
    }
  }, [ctx.view, ctx.search.query, runBatch, runCollab, runSearch]);

  // Batch Matches hides non-matches behind the "Show N non-matches" expander:
  // effectiveTotal drives both the visible slice below and the count/pageCount
  // ResultsPanel renders. Because `rows` is already ordered matches-then-
  // non-matches (see runBatch), clamping the slice to effectiveTotal is what
  // actually keeps hidden non-match rows off the page — Search/Shortlist/
  // Declined are unaffected (effectiveTotal === rows.length there).
  const nonMatchCount = ctx.view === 'batch' ? rows.length - matchCount : 0;
  const effectiveTotal = ctx.view === 'batch' ? (showNonMatches ? rows.length : matchCount) : rows.length;

  const visibleRows = useMemo(() => {
    const start = (ctx.page - 1) * PAGE_SIZE;
    if (start >= effectiveTotal) return [];
    return rows.slice(start, Math.min(start + PAGE_SIZE, effectiveTotal));
  }, [rows, ctx.page, effectiveTotal]);

  // Two-source progressive hydration (rule 6): render each visible row
  // immediately from the list payload (already true via `rows`), then
  // hydrate (a) ATS identity/contact in one batched candidatesByIds call and
  // (b) recsys profile per-candidate, staggered ~85ms apart. Applicant mode
  // (kan-119) adds (c): the candidates' JobApplicant rows on the job, one
  // batched jobApplicantsForCandidates call — the id the modal's Set Status /
  // Decline act on, plus the row's application date + status pill.
  // Only the pool tabs carry applicant rows; Shortlist/Declined never do.
  const hydrateApplicantsFor =
    applicantMode && (ctx.view === 'batch' || ctx.view === 'search' || ctx.view === 'collab')
      ? ctx.jobId
      : null;
  useEffect(() => {
    const token = ++hydrateTokenRef.current;
    const ids = visibleRows.map((r) => r.candidateId);

    setHydratedMap((prev) => {
      const next = { ...prev };
      visibleRows.forEach((row) => {
        const existing = next[row.candidateId];
        next[row.candidateId] = existing ? { ...existing, row } : emptyHydratedRow(row);
      });
      return next;
    });

    if (!ids.length) return;

    apolloClient
      .query<{ candidatesByIds: AtsCandidate[] }>({
        query: CANDIDATES_BY_IDS,
        variables: { ids },
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        if (hydrateTokenRef.current !== token) return;
        const byId = new Map(res.data.candidatesByIds.map((c) => [c.id, c]));
        setHydratedMap((prev) => {
          const next = { ...prev };
          ids.forEach((id) => {
            const existing = next[id];
            if (!existing) return;
            next[id] = { ...existing, ats: byId.get(id), atsLoaded: true };
          });
          return next;
        });
      })
      .catch(() => {
        if (hydrateTokenRef.current !== token) return;
        setHydratedMap((prev) => {
          const next = { ...prev };
          ids.forEach((id) => {
            const existing = next[id];
            if (existing) next[id] = { ...existing, atsLoaded: true };
          });
          return next;
        });
      });

    if (hydrateApplicantsFor) {
      const markApplicants = (byCandidate: Map<string, RecsysRowApplicant>): void => {
        setHydratedMap((prev) => {
          const next = { ...prev };
          ids.forEach((id) => {
            const existing = next[id];
            if (!existing) return;
            next[id] = { ...existing, applicant: byCandidate.get(id), applicantLoaded: true };
          });
          return next;
        });
      };
      apolloClient
        .query<{ jobApplicantsForCandidates: (RecsysRowApplicant & { candidateId: string })[] }>({
          query: JOB_APPLICANTS_FOR_CANDIDATES,
          variables: { jobOrderId: hydrateApplicantsFor, candidateIds: ids },
          fetchPolicy: 'network-only',
        })
        .then((res) => {
          if (hydrateTokenRef.current !== token) return;
          markApplicants(
            new Map(
              res.data.jobApplicantsForCandidates.map(
                ({ candidateId, id, JobAppStatus, createdAt, aiScore, aiScoreReason, aiScoredResumeId, aiScoredAt }) => [
                  candidateId,
                  {
                    id,
                    JobAppStatus,
                    createdAt,
                    aiScore,
                    // kan-120: the row badge's modal needs the reason/résumé/date.
                    aiScoreReason: aiScoreReason ?? null,
                    aiScoredResumeId: aiScoredResumeId ?? null,
                    aiScoredAt: aiScoredAt ?? null,
                  },
                ]
              )
            )
          );
        })
        .catch(() => {
          // No application data: the row shows "Application not found" and the
          // modal stays read-only (see the CandidateWorkspacePanel call below).
          if (hydrateTokenRef.current !== token) return;
          markApplicants(new Map());
        });
    }

    const timers: number[] = [];
    ids.forEach((id, idx) => {
      setHydratedMap((prev) => {
        const existing = prev[id];
        if (!existing) return prev;
        return { ...prev, [id]: { ...existing, profileStatus: 'loading' } };
      });

      const timer = window.setTimeout(() => {
        if (hydrateTokenRef.current !== token) return;
        recsysClient
          .candidateProfile(id)
          .then((profile) => {
            if (hydrateTokenRef.current !== token) return;
            setHydratedMap((prev) => {
              const existing = prev[id];
              if (!existing) return prev;
              return { ...prev, [id]: { ...existing, profile, profileStatus: 'loaded' } };
            });
          })
          .catch((err) => {
            if (hydrateTokenRef.current !== token) return;
            const status = err instanceof RecsysHttpError && err.status === 404 ? 'removed' : 'error';
            setHydratedMap((prev) => {
              const existing = prev[id];
              if (!existing) return prev;
              return { ...prev, [id]: { ...existing, profileStatus: status } };
            });
          });
      }, idx * HYDRATE_STAGGER_MS);
      timers.push(timer);
    });

    // Cancel any still-pending stagger timers when the visible page changes or
    // the page unmounts. The hydrateTokenRef guard already blocks stale
    // setState; this just avoids firing profile fetches we no longer need.
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRows, apolloClient, hydrateApplicantsFor]);

  const hydratedVisible = useMemo(
    () => visibleRows.map((row) => hydratedMap[row.candidateId] ?? emptyHydratedRow(row)),
    [visibleRows, hydratedMap]
  );

  // A center resolution in flight (kan-83) disables the slider too: the radius
  // it would set belongs to a center that is about to change.
  const radiusDisabled =
    ctx.geoResolve.status === 'resolving' || ctx.filters.lat === null || ctx.filters.lng === null;
  const detailItem = ctx.detailId ? hydratedMap[ctx.detailId] ?? null : null;
  const applicantPanel = applicantPanelProps(detailItem);

  // kan-120: the row's AiScoreBadge keeps a fetched / rescored score in its own
  // state; write it back into the hydrated row too so paging away and back
  // (the badge remounts from props) and the detail modal agree with it.
  // Only merges into an applicant that exists — never invents one. Stable so
  // rows don't re-render on every parent render.
  const handleApplicantScoreUpdated = useCallback((candidateId: string, score: ApplicantScoreUpdate) => {
    setHydratedMap((prev) => {
      const existing = prev[candidateId];
      if (!existing?.applicant) return prev;
      return { ...prev, [candidateId]: { ...existing, applicant: { ...existing.applicant, ...score } } };
    });
  }, []);

  // Drops a candidate from the CURRENT rows list and re-ranks — same UX as
  // the existing onApplied drop. Only meaningful on the pool tabs
  // Batch/Search/Collaborated Search (the
  // Shortlist/Declined tabs re-derive their rows from ctx.shortlistIds /
  // ctx.declinedIds once the mutation's refetch resolves). On Batch, `rows`
  // is match-then-non-match ordered (runBatch), so dropping a row ahead of
  // matchCount also decrements it — otherwise the header count and the "Show
  // N non-matches" expander would drift after an Add/Decline.
  const dropRow = useCallback(
    (candidateId: string) => {
      const idx = rows.findIndex((r) => r.candidateId === candidateId);
      if (idx === -1) return;
      setRows((prev) => prev.filter((r) => r.candidateId !== candidateId).map((r, i) => ({ ...r, rank: i + 1 })));
      if (ctx.view === 'batch' && idx < matchCount) {
        setMatchCount((prev) => Math.max(0, prev - 1));
      }
    },
    [rows, matchCount, ctx.view]
  );

  // Story 2b: Add to Shortlist / Decline from the Batch/Search row actions or
  // the detail modal. Drops the row locally (instant UX) — ctx.shortlist /
  // ctx.decline refetch the disposition query, which bumps the relevant tab
  // count.
  // The ctx.* handlers toast their own errors and re-reject; the row is dropped
  // only on success (inside .then), and .catch swallows the rejection so it
  // doesn't surface as an unhandled promise rejection.
  const handleShortlistRow = useCallback(
    (candidateId: string) => {
      ctx.shortlist(candidateId).then(() => {
        if (ctx.view === 'batch' || ctx.view === 'search' || ctx.view === 'collab') dropRow(candidateId);
      }).catch(() => {/* already toasted in context */});
    },
    [ctx, dropRow]
  );

  const handleDeclineRow = useCallback(
    (candidateId: string) => {
      ctx.decline(candidateId).then(() => {
        if (ctx.view === 'batch' || ctx.view === 'search' || ctx.view === 'collab') dropRow(candidateId);
      }).catch(() => {/* already toasted in context */});
    },
    [ctx, dropRow]
  );

  const handleShortlistFromDetail = useCallback(
    async (candidateId: string) => {
      await ctx.shortlist(candidateId);
      if (ctx.view === 'batch' || ctx.view === 'search' || ctx.view === 'collab') dropRow(candidateId);
      ctx.setDetailId(null);
    },
    [ctx, dropRow]
  );

  const handleDeclineFromDetail = useCallback(
    async (candidateId: string) => {
      await ctx.decline(candidateId);
      if (ctx.view === 'batch' || ctx.view === 'search' || ctx.view === 'collab') dropRow(candidateId);
      ctx.setDetailId(null);
    },
    [ctx, dropRow]
  );

  // Declined candidate opened in the modal: Restore is the only action —
  // removes the DECLINED disposition; the refetched declinedIds drop the row.
  const handleRestoreFromDetail = useCallback(
    async (candidateId: string) => {
      await ctx.restore(candidateId);
      ctx.setDetailId(null);
    },
    [ctx]
  );

  // Declined tab: Restore removes the disposition — the row disappears from
  // this list once the refetched declinedIds no longer include it.
  const handleRestoreRow = useCallback(
    (candidateId: string) => {
      ctx.restore(candidateId).catch(() => {/* already toasted in context */});
    },
    [ctx]
  );

  // Applicant mode (kan-119): the modal's JobApplicant actions. A status set
  // that closes out the applicant (Keep / Contact Later / Call Scheduled) or a
  // Decline takes them out of the "new applicants" pool, so the row drops and
  // the modal closes — the /new-applicants queue's markProcessed + close.
  // The panel reports the APPLICANT id; the open modal is always ctx.detailId's
  // candidate, so that is the row to drop.
  const handleApplicantClosedOut = useCallback(() => {
    if (ctx.detailId) dropRow(ctx.detailId);
    ctx.setDetailId(null);
  }, [ctx, dropRow]);

  // Called / Emailed: the panel deliberately stays open and switches to its
  // Notes tab so the recruiter can log the call (which also grants ownership)
  // — /new-applicants does the same. The row still leaves the pool (it is no
  // longer Applied), but closing here would throw the recruiter out of the
  // note they are about to write, so this drops WITHOUT closing.
  const handleApplicantProcessed = useCallback(() => {
    if (ctx.detailId) dropRow(ctx.detailId);
  }, [ctx.detailId, dropRow]);

  const toggleSelectRow = useCallback((candidateId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(candidateId)) next.delete(candidateId);
      else next.add(candidateId);
      return next;
    });
  }, []);

  // Shortlist tab: per-row Apply (single-candidate variant of bulk apply).
  const handleApplyRow = useCallback(
    (candidateId: string) => {
      ctx.bulkApply([candidateId]).then(() => {
        dropRow(candidateId);
        setSelectedIds((prev) => {
          if (!prev.has(candidateId)) return prev;
          const next = new Set(prev);
          next.delete(candidateId);
          return next;
        });
      }).catch(() => {/* already toasted in context */});
    },
    [ctx, dropRow]
  );

  const handleBulkApply = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkApplying(true);
    try {
      await ctx.bulkApply(ids);
      setRows((prev) => prev.filter((r) => !ids.includes(r.candidateId)).map((r, i) => ({ ...r, rank: i + 1 })));
      setSelectedIds(new Set());
    } catch {
      /* already toasted in context; keep rows so nothing silently vanishes */
    } finally {
      setBulkApplying(false);
    }
  }, [selectedIds, ctx]);

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '16px 16px 40px' }}>
      <div style={HEADER_STYLE}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', lineHeight: 1.2 }}>
            Candidate Search
          </div>
          <div style={{ fontSize: 12, color: '#667085', marginTop: 2 }}>
            AI-ranked candidates for the selected job order
          </div>
        </div>
        {isExecutive && <FreshnessPill ingest={ctx.ingest} />}
      </div>

      {isExecutive && <StaleBanner ingest={ctx.ingest} onRefresh={handleRefresh} />}
      {/* Everyone sees the nightly-window notice (StaleBanner above is Executive-only). */}
      <MaintenanceBanner />

      <div style={{ marginTop: 16 }}>
        <SelectionPanel />
      </div>

      <div style={{ marginTop: 16 }}>
        <TabBar />
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 262px', minWidth: 262 }}>
          <FiltersRail
            filters={ctx.filters}
            onChange={ctx.setFilters}
            onCenterCommit={ctx.commitGeoCenter}
            onClearAll={ctx.clearFilters}
            radiusDisabled={radiusDisabled}
            geoResolveStatus={ctx.geoResolve.status}
            geoFormattedAddress={ctx.geoResolve.formattedAddress}
          />
        </div>

        <div style={{ flex: '1 1 400px', minWidth: 0 }}>
          {ctx.view === 'batch' && (
            <BatchControlCard
              topN={ctx.topN}
              onTopN={ctx.setTopN}
              mustHave={ctx.mustHave}
              onMustHave={ctx.setMustHave}
              briefUsed={briefUsed}
              computedAt={computedAt}
              onRefresh={runBatch}
              loading={rowsLoading}
            />
          )}
          {ctx.view === 'search' && (
            <SearchModeControl
              mode={ctx.search.mode}
              query={ctx.search.query}
              topN={ctx.topN}
              onMode={ctx.setSearchMode}
              onQuery={ctx.setSearchQuery}
              onTopN={ctx.setTopN}
              onSearch={runSearch}
            />
          )}

          {/* Collaborated Search run card (kan-82). No Top-N / must-have
              controls of its own — it reuses the shared Top-N on the next run. */}
          {ctx.view === 'collab' && (
            <div style={COLLAB_CARD_STYLE}>
              <div style={{ fontSize: 13, color: '#667085', marginBottom: 14 }}>
                Finds candidates whose résumés are similar to the ones you&apos;ve already interviewed
                on this job order.
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={rowsLoading}
                onClick={runCollab}
              >
                {rowsLoading ? 'Searching…' : 'Find Similar Candidates'}
              </button>
            </div>
          )}

          {ctx.view === 'shortlist' && selectedIds.size > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#FEF0D9',
                border: '1px solid #FBDBA0',
                borderRadius: 8,
                padding: '10px 14px',
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 13, color: '#1F2937' }}>
                {selectedIds.size} candidate{selectedIds.size === 1 ? '' : 's'} selected
              </span>
              <button
                type="button"
                className="btn btn-warning btn-sm"
                disabled={bulkApplying}
                onClick={handleBulkApply}
              >
                {bulkApplying ? 'Applying…' : `Apply ${selectedIds.size} to job order`}
              </button>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <ResultsPanel
              view={ctx.view}
              hydrated={hydratedVisible}
              total={effectiveTotal}
              page={ctx.page}
              onPage={ctx.setPage}
              loading={rowsLoading}
              tooBroad={tooBroad}
              backendError={backendError}
              awaitingRun={
                (ctx.view === 'batch' && batchAwaitingRun) || (ctx.view === 'collab' && collabAwaitingRun)
              }
              noInterviews={ctx.view === 'collab' && collabNoInterviews}
              collabSeeds={ctx.view === 'collab' ? collabSeeds : null}
              matchCount={matchCount}
              nonMatchCount={nonMatchCount}
              showNonMatches={showNonMatches}
              onToggleNonMatches={() => setShowNonMatches((v) => !v)}
              onOpenDetail={ctx.setDetailId}
              selectable={ctx.view === 'shortlist'}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelectRow}
              dispositionMode={
                ctx.view === 'shortlist'
                  ? 'shortlist'
                  : ctx.view === 'declined'
                  ? 'declined'
                  : applicantMode
                  ? 'applicants'
                  : null
              }
              onApplyRow={handleApplyRow}
              onRestoreRow={handleRestoreRow}
              onShortlistRow={handleShortlistRow}
              onDeclineRow={handleDeclineRow}
              onApplicantScoreUpdated={handleApplicantScoreUpdated}
            />
          </div>
        </div>
      </div>

      {/*
        Reuse the /new-applicants candidate modal verbatim — same width (better
        résumé view) + Notes + Apply tabs. The recsys candidate isn't a
        JobApplicant yet, so there's no applicant id/status; the Apply tab is the
        path that creates one. candidatesByIds returns the exact candidate shape
        this panel reads. onShortlist/onDecline (Story 2b) enable the header's
        "Add to Shortlist"/"Decline" buttons and drive the disposition mutations.

        Applicant mode (kan-119) renders the panel the /new-applicants way
        instead: the candidate IS a JobApplicant on this job, so it gets the
        applicant row (id/status/date) + SET_STATUS_OPTIONS and NONE of the
        recsys-disposition props (onShortlist/onDecline/onRestore/
        dispositionStatus/defaultApplyTarget/applySource) — any one of those
        flips the panel into recsys-disposition mode and hides Set Status /
        Decline. applicantPanelProps (utils.ts) always passes the candidate
        identity but keeps the panel read-only (showDisabledDispositions: the
        disabled placeholders show, Set Status + Decline are hidden) until the
        applicant id has hydrated — both handlers dereference applicant.id —
        and re-keys the panel when it lands so the status badge is seeded.
      */}
      {applicantMode && (ctx.view === 'batch' || ctx.view === 'search' || ctx.view === 'collab') ? (
        <CandidateWorkspacePanel
          key={applicantPanel.key}
          isOpen={!!ctx.detailId}
          toggle={() => ctx.setDetailId(null)}
          applicant={applicantPanel.applicant}
          showDisabledDispositions={applicantPanel.showDisabledDispositions}
          jobOrderId={ctx.jobId ?? undefined}
          setStatusOptions={SET_STATUS_OPTIONS}
          onStatusChange={handleApplicantClosedOut}
          onDeclined={handleApplicantClosedOut}
          onProcessed={handleApplicantProcessed}
          gradingJobOrderId={ctx.jobId ?? undefined}
        />
      ) : (
        <CandidateWorkspacePanel
          isOpen={!!ctx.detailId}
          toggle={() => ctx.setDetailId(null)}
          applicant={detailItem?.ats ? { candidate: detailItem.ats } : null}
          onShortlist={handleShortlistFromDetail}
          onDecline={handleDeclineFromDetail}
          onRestore={handleRestoreFromDetail}
          gradingJobOrderId={ctx.jobId ?? undefined}
          applySource="Candidate Rec"
          defaultApplyTarget={
            // Prefill the Apply tab with the page's current company + job so
            // applying to the job being matched against is one click.
            ctx.activeJob
              ? {
                  companyId: ctx.activeJob.company.id,
                  companyName: ctx.activeJob.company.name,
                  jobOrderId: ctx.activeJob.id,
                }
              : null
          }
          dispositionStatus={
            // Current disposition of the open candidate drives which header
            // actions render: SHORTLISTED hides "Add to Shortlist", DECLINED
            // shows only "Restore", null (Batch/Search pool) shows both.
            ctx.detailId && ctx.declinedIds.includes(ctx.detailId)
              ? 'DECLINED'
              : ctx.detailId && ctx.shortlistIds.includes(ctx.detailId)
              ? 'SHORTLISTED'
              : null
          }
          onApplied={(appliedCandidateId, appliedJobOrderId) => {
            // Applying to the CURRENTLY-selected job makes the candidate part of
            // its pipeline — drop them from the results immediately (mirrors the
            // server-side appliedCandidateIdsForJob exclusion on next fetch).
            // Applying to a different job leaves this list untouched.
            if (appliedJobOrderId === ctx.jobId) {
              dropRow(appliedCandidateId);
            }
          }}
        />
      )}
    </div>
  );
};

const CandidateRecommendations: FC = () => (
  <RecPageProvider>
    <CandidateRecommendationsContent />
  </RecPageProvider>
);

export default CandidateRecommendations;
